import { Button } from 'antd';
import classnames from 'classnames';
import noop from 'lodash/noop';
import moment from 'moment';
import React from 'react';

import { ModalMask } from '@components/common';
import { uuidv4 } from '@utils';

import Calendar from './Calendar';
import Display from './Display';
import Shortcuts from './Shortcuts';
import styles from './styles/DatePicker.less';
import IntervalSelector from './IntervalSelector';

const CUSTOM = 'custom';

class DatePicker extends React.PureComponent<any, any> {
  display: React.RefObject<HTMLDivElement>;
  modalId: string;
  constructor(props) {
    super(props);
    const { defaultValue, defaultShortcut } = props;
    this.state = {
      interval: defaultValue,
      selectedShortcut: defaultShortcut,
      open: false,
      durationReachLimit: false,
    };
    this.display = React.createRef<HTMLDivElement>();
    this.modalId = uuidv4();
  }

  componentDidMount() {
    if (this.display.current) {
      this.display.current.addEventListener('click', this.toggleOpen);
    }
    document.addEventListener('mousedown', this.clickModalBg);
  }

  componentWillUnmount() {
    if (this.display.current) {
      this.display.current.removeEventListener('click', this.toggleOpen);
    }
    document.removeEventListener('mousedown', this.clickModalBg);
  }

  clickModalBg = (e) => {
    const { onCancel } = this.props;
    if (e.target.id === this.modalId) {
      onCancel();
      setTimeout(() => {
        this.toggleOpen();
      }, 0);
    }
  };

  setDate = (dates) => {
    this.setState({
      interval: dates,
      selectedShortcut: CUSTOM,
    });
  };

  toggleOpen = () => {
    const { defaultValue, defaultShortcut, onClose, onOpen } = this.props;
    const { open } = this.state;
    if (open) {
      onClose();
      this.setState({
        open: false,
        interval: defaultValue,
        selectedShortcut: defaultShortcut,
      });
    } else {
      onOpen();
      this.setState({ open: true });
    }
  };

  close = () => {
    const { onClose } = this.props;
    onClose();
    this.setState({
      open: false,
    });
  };

  reset = () => {
    const { defaultValue, defaultShortcut } = this.props;
    this.setState({
      interval: defaultValue,
      selectedShortcut: defaultShortcut,
    });
  };

  onApply = () => {
    const { interval, selectedShortcut } = this.state;
    const { onApply, onClose } = this.props;
    this.setState({
      open: false,
    });
    onApply(interval, selectedShortcut);
    onClose();
  };

  updateDate = (interval) => {
    const { onChange, fixedRange } = this.props;
    const [from, to] = interval;
    let newInterval;

    // both onChange and onCalendarChange are triggered for each click
    // for compare dod picker, set fixed range based on start value
    if (fixedRange === false) {
      /* CE-7280: change the 'to' to endOf minute to change the interval end
       IF server now adds a padding of 10 seconds rather than 1 minute 
       as a workaround for end interval being exclusive in the Druid.
       'to' before change : 23:59:00 
       'to' after change : 23:59:59
      */
      newInterval = [from.startOf('day'), to.endOf('day').endOf('minute')];
    } else {
      newInterval = this.formatCompareInterval(interval, fixedRange);
    }

    onChange({ interval: newInterval, selectedShortcut: CUSTOM });
    this.setState({
      interval: newInterval,
      selectedShortcut: CUSTOM,
    });
  };

  updateInterval = (interval) => {
    const { interval: oldInterval } = this.state;
    const { onChange, fixedRange } = this.props;
    let newInterval;
    if (interval.length === 1) {
      if (fixedRange === false) {
        newInterval = [interval[0], oldInterval[1]];
      } else {
        newInterval = [interval[0], moment(interval[0]).add(fixedRange, 'minutes')];
      }
    } else if (fixedRange === false) {
      newInterval = interval;
    } else {
      newInterval = [moment(interval[1]).subtract(fixedRange, 'minutes'), interval[1]];
    }
    onChange({ interval: newInterval, selectedShortcut: CUSTOM });
    this.setState({ interval: newInterval, selectedShortcut: CUSTOM });
  };

  onShortcutChange = (interval, selectedShortcut) => {
    const { onChange, durationAllowed } = this.props;
    onChange({
      interval,
      selectedShortcut,
    });
    if (interval) {
      const [from, to] = interval;
      if (moment(to).diff(from, 'months') <= durationAllowed) {
        this.setState({
          interval,
          selectedShortcut,
        });
        return;
      }
    }
    this.setState({ selectedShortcut });
  };

  // set end date based on start date
  // compare range should match main interval
  formatCompareInterval = (dates, fixedRange) => {
    const newFrom = moment(dates[0].format());
    const newTo = moment(newFrom.format()).add(fixedRange, 'minutes');
    return [newFrom, newTo];
  };

  // both onChange and onCalendar change are triggerd for each click
  onCalendarChange = (dates) => {
    const { fixedRange, onChange } = this.props;
    if (fixedRange !== false) {
      const newInterval = this.formatCompareInterval(dates, fixedRange);
      onChange({ interval: newInterval, selectedShortcut: CUSTOM });
      this.setState({
        interval: newInterval,
      });
    }
  };

  durationReachLimit = () => {
    const { interval } = this.state;
    const from = moment(interval[0].format());
    const to = moment(interval[1].format());
    const diffDays = to.diff(from, 'day');
    if (diffDays >= this.props.durationLimit) {
      this.setState({
        durationReachLimit: true,
      });
      return true;
    }
    this.setState({
      durationReachLimit: false,
    });
    return false;
  };

  ousideOfCalenedryBoundry = () => {
    const { interval } = this.state;
    const { calenderBoundry } = this.props;
    const from = moment(interval[0]);
    const to = moment(interval[1]);
    const boundaryStart = moment(calenderBoundry.start_time);
    const boundaryEnd = moment(calenderBoundry.end_time);
    const isBefore = to.isBefore(boundaryStart) && from.isBefore(boundaryStart);
    const isAfter = from.isAfter(boundaryEnd) && to.isAfter(boundaryEnd);
    return isBefore || isAfter;
  };

  render() {
    const {
      disabledDate,
      shortcuts,
      title,
      className,
      expanded,
      granularity,
      fixedRange,
      placement,
      live,
    } = this.props;
    const { interval, open, selectedShortcut } = this.state;
    return (
      <>
        {open && <ModalMask id={this.modalId} />}
        <div className={styles.datePicker}>
          <div ref={this.display}>
            <Display
              expanded={expanded}
              className={className}
              interval={interval}
              selected={open}
              title={title}
              shortcut={selectedShortcut}
            />
          </div>
          {open && (
            // eslint-disable-next-line react/jsx-no-duplicate-props
            <div className={classnames(styles.popup, { [styles.openLeft]: placement === 'left' })}>
              {shortcuts.length > 0 && (
                <Shortcuts
                  live={live}
                  shortcuts={shortcuts}
                  onChange={this.onShortcutChange}
                  selected={selectedShortcut}
                  showCustom={this.props.showCustom}
                />
              )}
              <div className={styles.main}>
                <Calendar
                  defaultValue={interval}
                  disabledDate={disabledDate}
                  onChange={this.updateDate}
                  onCalendarChange={this.onCalendarChange}
                  forceRender={fixedRange !== false}
                />
                <div className={styles.separator} />
                <div className={styles.intervalSelector}>
                  {
                    <IntervalSelector
                      onChange={this.updateInterval}
                      interval={interval}
                      granularity={granularity}
                    />
                  }
                </div>
                <Button
                  id="date-picker-apply"
                  className={styles.applyBtn}
                  type="primary"
                  ghost
                  onClick={this.onApply}
                  disabled={
                    interval[0].isSameOrAfter(interval[1], 'minute') ||
                    this.durationReachLimit() ||
                    this.ousideOfCalenedryBoundry()
                  }
                  style={{ fontWeight: 500 }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

DatePicker.defaultProps = {
  className: '',
  defaultValue: [],
  disabledDate: noop,
  shortcuts: [],
  defaultShortcut: CUSTOM,
  title: '',
  expanded: false,
  granularity: 5,
  onOpen: noop,
  onClose: noop,
  onApply: noop,
  onCancel: noop,
  onChange: noop,
  fixedRange: false,
  durationAllowed: 13, // months
  durationLimit: Infinity, // days
  placement: '',
  live: false,
};

export default DatePicker;
